<template>
    <div v-if="course || courseTemplate">
        <ul>
            <li class="is-sidebar-summary-item is-collection">
                <ul class="tw:justify-center tw:items-center">
                    <app-sidebar-summary-item
                        v-if="!isCourseTemplate"
                        id="application_sidebar_summary_course_dashboard"
                        type="is-primary"
                        label="Course Dashboard"
                        icon="pi pi-objects-column"
                        :to="{ name: 'course-dashboard', params: { courseId } }"
                    />

                    <app-sidebar-summary-item
                        v-if="canShowGradebookPreview"
                        type="is-primary"
                        label="Gradebook Preview"
                        icon="pi pi-chart-line"
                        :to="{
                            name: 'course-template-gradebook-preview',
                            params: { id: $route.params.id },
                        }"
                    />

                    <app-sidebar-summary-item
                        v-else-if="!isCourseTemplate && course && $gate.allows('viewPreview', ['Gradebook', course])"
                        type="is-primary"
                        label="Gradebook Preview"
                        icon="pi pi-chart-line"
                        :to="{
                            name: 'course-gradebook-preview',
                            params: { courseId },
                        }"
                    />

                    <app-sidebar-summary-item
                        v-if="!isCourseTemplate && $gate.allows('view', ['Gradebook', course])"
                        type="is-primary"
                        label="Gradebook"
                        icon="pi pi-chart-bar"
                        :to="{
                            name: 'gradebook-view',
                            params: {
                                courseId,
                                gradebookId: get(course, 'gradebook.id'),
                            },
                        }"
                    />

                    <app-sidebar-summary-item
                        v-if="!isCourseTemplate && $gate.allows('viewStudent', ['Gradebook', course])"
                        type="is-primary"
                        label="Grades"
                        icon="pi pi-chart-bar"
                        :to="{
                            name: 'student-grade-view',
                            params: { courseId: course.id, userId: user.id },
                        }"
                    />

                    <app-sidebar-summary-item
                        v-if="
                            !isCourseTemplate && $gate.allows('viewCourseEnrollmentPage', ['CourseEnrollment', course])
                        "
                        type="is-primary-dark"
                        label="Enrollments"
                        icon="pi pi-users"
                        :to="{
                            name: 'course-enrollments-view',
                            params: { courseId },
                        }"
                    />
                </ul>
            </li>

            <li
                v-if="user && course && (user.hasPermissionViaCourseTo(course, 'view a course') || user.is_admin)"
                class="is-sidebar-summary-item is-collection"
            >
                <ul v-if="shouldDisplayCourseContentMenuItems">
                    <app-sidebar-summary-item
                        v-if="hasWeeksItem"
                        :class="{ 'is-active': hasPresentWeek }"
                        label="Click to View All Weeks"
                        type="is-primary-light"
                        :icon="getWeeksIcon"
                        :title="getWeeksTitle"
                        @open="open('course-content')"
                    />

                    <app-sidebar-summary-item
                        v-if="items.curriculum.misc_pre.length || items.curriculum.misc_post.length"
                        type="is-primary-light"
                        icon="pi pi-folder-open"
                        label="Click to Expand"
                        @open="open('course-content')"
                    />
                </ul>
                <ul v-else>
                    <e-icon icon="pi pi-folder" class="tw:text-gray-400 tw:cursor-not-allowed" />
                </ul>
            </li>

            <li
                v-if="!isCourseTemplate && (hasNotesMenu || hasCourseReportsMenu)"
                class="is-sidebar-summary-item is-collection"
            >
                <ul>
                    <app-sidebar-summary-item
                        v-if="course && !isSelfPacedCourse && $gate.allows('viewAny', ['Attendance', course])"
                        label="Attendance"
                        type="is-primary-dark"
                        icon="pi pi-address-book"
                        :to="{ name: 'course-attendance' }"
                    />

                    <app-sidebar-summary-item
                        v-if="course && $can('view all', 'meetings')"
                        label="Meetings"
                        type="is-primary-dark"
                        icon="pi pi-video"
                        :to="{ name: 'course-meetings-view-upcoming' }"
                    />

                    <app-sidebar-summary-item
                        v-if="course && $gate.allows('viewAny', ['AttemptAuthorization', course])"
                        label="Attempt Authorizations"
                        type="is-primary-dark"
                        icon="pi pi-key"
                        :to="{ name: 'course-attempt-authorizations-view' }"
                    />

                    <app-sidebar-summary-item
                        v-if="$gate.allows('viewAny', ['QuizDashboard'])"
                        label="Quiz Dashboard"
                        type="is-primary-dark"
                        icon="pi pi-key"
                        :to="{ name: 'quiz-dashboard' }"
                    />

                    <app-sidebar-summary-item
                        v-if="course && hasNotesMenu"
                        label="My Notes"
                        type="is-primary-dark"
                        icon="pi pi-book"
                        :to="{ name: 'course-notes' }"
                    />

                    <app-sidebar-summary-item
                        v-if="course && $gate.allows('viewReports', course)"
                        label="Course Reports"
                        type="is-primary"
                        icon="pi pi-file"
                        :to="{ name: 'discussion-thread-report' }"
                    />

                    <app-sidebar-summary-item
                        v-if="course && $gate.allows('viewAny', 'Remediation')"
                        label="Remediation"
                        type="is-primary"
                        icon="file-heart"
                        :to="{ name: 'course-remediation' }"
                        :has-badge="canViewRemediation"
                        :badge-count="pendingGradeRemediations.length"
                    />

                    <app-sidebar-summary-item
                        v-if="course && $gate.allows('viewAny', ['CourseLogEntry', course])"
                        label="Course Log"
                        type="is-primary"
                        icon="pi pi-pen-to-square"
                        :has-badge="get(course, 'log_entries_count', 0) > 0"
                        :badge-count="get(course, 'log_entries_count', 0)"
                        @open="openCourseLog = true"
                    />

                    <app-sidebar-summary-item
                        v-if="course && canShowScheduleMenu"
                        :label="scheduleMenuLabel"
                        type="is-primary"
                        icon="pi pi-calendar"
                        :to="{ name: scheduleMenuRouteName }"
                    />
                </ul>
            </li>
        </ul>

        <e-modal v-if="courseId" v-model="openCourseLog" width="700" has-modal-card>
            <course-log-entries :course-id="courseId" />
        </e-modal>
    </div>
</template>

<script>
import get from 'lodash/get.js';
import { mapState } from 'vuex';

import AppSidebarSummaryItem from '@/components/app/sidebar/AppSidebarSummaryItem.vue';
import CourseLogEntries from '@/components/course-log/CourseLogEntries.vue';
import { isCourseSelfPaced } from '@/composables/useSelfPacedCourse.js';
import Course from '@/models/Course.js';
import CourseTemplate from '@/models/CourseTemplate.js';
import Remediation from '@/models/Remediation.js';

export default {
    name: 'AppSidebarSummary',

    components: { AppSidebarSummaryItem, CourseLogEntries },

    props: {
        items: {
            default: null,
            type: null,
        },

        hasNotesMenu: {
            default: false,
            type: Boolean,
        },

        hasCourseReportsMenu: {
            default: false,
            type: Boolean,
        },

        courseId: {
            type: Number,
            required: true,
        },
    },

    emits: ['open'],

    data: () => ({
        openCourseLog: false,
    }),

    computed: {
        ...mapState('Auth', { user: state => state.user }),

        shouldDisplayCourseContentMenuItems() {
            return (
                this.items.curriculum.misc_pre.length || this.hasPresentWeek || this.items.curriculum.misc_post.length
            );
        },

        isCourseTemplate() {
            return this.$route.name.includes('template');
        },

        /**
         * @returns {boolean}
         */
        hasWeeksItem() {
            const { weeks } = this.items.curriculum;

            return weeks.past.length || weeks.present.length || weeks.future.length;
        },

        getWeeksIcon() {
            if (this.hasPresentWeek && this.items.curriculum.weeks.present[0].section_type === 'break') {
                return 'sunset';
            }

            return 'calendar-alt';
        },

        /**
         * @returns {string|null}
         */
        getWeeksTitle() {
            if (!this.hasWeeksItem) return null;

            let { weeks } = this.items.curriculum;

            if (this.hasPresentWeek) {
                const presentWeek = weeks.present[0];

                if (presentWeek.section_type === 'break') {
                    return 'Break';
                }

                return `Week ${presentWeek.display_week_number}`;
            }

            if (weeks.past.length) {
                weeks = weeks.past;
            } else if (weeks.future.length) {
                weeks = weeks.future;
            } else {
                return null;
            }

            weeks = weeks.filter(week => week.section_type === 'week');

            return weeks.length > 1
                ? `Weeks ${weeks[0].display_week_number}-${weeks[weeks.length - 1].display_week_number}`
                : `Week ${weeks[0].display_week_number}`;
        },

        /**
         * @returns {boolean}
         */
        hasPresentWeek() {
            if (!this.hasWeeksItem) return false;

            return !!this.items.curriculum.weeks.present.length;
        },

        course() {
            return Course.query().with('gradebook').find(this.courseId);
        },

        isSelfPacedCourse() {
            return isCourseSelfPaced(this.course);
        },

        courseTemplate() {
            return CourseTemplate.query().find(this.$route.params.id);
        },

        canViewRemediation() {
            return this.user.hasPermissionTo('view all remediation processes');
        },

        remediations() {
            return Remediation.query().where('course_id', this.courseId).get();
        },

        pendingGradeRemediations() {
            return this.remediations.filter(remediation => {
                return remediation.status === 'Pending Instructor Grade';
            });
        },

        canShowScheduleMenu() {
            if (this.isSelfPacedCourse) {
                return this.$can('view all', 'meetings');
            }

            return this.$gate.allows('viewAny', 'TimePeriod');
        },

        scheduleMenuLabel() {
            return this.isSelfPacedCourse ? 'Meetings' : 'Due Dates and Schedule';
        },

        scheduleMenuRouteName() {
            return this.isSelfPacedCourse ? 'course-meetings-view-upcoming' : 'course-activities-view-upcoming';
        },

        canShowGradebookPreview() {
            return (
                this.isCourseTemplate &&
                this.courseTemplate &&
                this.$gate.allows('viewTemplatePreview', ['Gradebook', this.courseTemplate])
            );
        },
    },

    methods: {
        get,

        /**
         * @param event
         */
        open(event) {
            this.$emit('open', event);
        },
    },
};
</script>

<style lang="scss" scoped>
@import 'resources/scss/toolbox.scss';

$divider-height: 3px;

/* Ensure all ULs in the sidebar summary take full width and center content */
ul {
    list-style: none;
    padding: 0;
    margin: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem
}

.is-sidebar-summary-item {
    text-align: center;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;

    &-title {
        color: $text-subtle;
        display: block;
        font-size: $size-10;
        line-height: 1;
        margin-top: 0.3rem;
    }

    &-button {
        margin-top: $column-gap;
    }

    &.is-collection {
        margin-bottom: 0;
        position: relative;

        /* Nested UL within collection should also be full width */
        > ul {
            width: 100%;
        }

        &::before {
            background: var(--primary-color);
            content: '';
            display: block;
            height: $divider-height;
            margin: ($column-gap * 1.5) auto;
            width: 50%;
            opacity: 0.5;
        }

        &:first-child::before {
            display: none;
        }

        .is-sidebar-summary-item:first-child .button {
            margin-top: 0;
        }
    }

    &.is-active {
        position: relative;

        .button::after {
            background: var(--primary-color);
            border-radius: 3px;
            content: '';
            height: 6px;
            position: absolute;
            right: #{calc($column-gap / -1)};
            top: 50%;
            transform: translateY(-50%);
            width: 6px;
        }

        .is-sidebar-summary-item-title {
            color: var(--primary-color);
        }
    }

    .button {
        &.is-small {
            box-shadow: none !important;
        }
    }
}
</style>
